/* eslint-disable react-hooks/rules-of-hooks */
import {
  BaseBoxShapeUtil,
  HTMLContainer,
  SVGContainer,
  T,
  TLAssetId,
  TLImageAsset,
  TLOnBeforeCreateHandler,
  TLOnBeforeUpdateHandler,
  TLOnTranslateHandler,
  TLShapeId,
  TLShapePartial,
  Vec2d,
  assetIdValidator,
  createShapeId,
  deepCopy,
  toDomPrecision,
  useIsCropping,
  useValue,
} from '@tldraw/tldraw'
import {
  createBlobUrlFromSvg,
  extractColors,
  getContainerStyle,
  getDataURIFromURL,
  isSvgAsset,
  noCacheUrl,
  replaceSvgColors,
} from './helpers'
import { ColorSet, ImageShape, ImageShapeProps } from './Types'
import { useEffect, useState } from 'react'
import {
  CornerRoundingStyle,
  RemoveBackgroundStyle,
  StrokeColorStyle,
  StrokeWidthStyle,
} from 'components/VisualStudioPlayground/VisualEditor/StyleProps'
import { imageShapeMigrations } from './ImageShape'
import { roundCorners } from 'svg-round-corners'
import { CustomEqualitySet } from 'components/VisualStudioPlayground/VisualEditor/utils/customEqualitySet'
import { SolidColor } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { isEqual } from 'lodash'
import { fitAndPosition } from 'object-fit-math'
import { FrameShape } from '../FrameShape/Types'
import { findAssetByTransformations, getBackgroundImage } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { isUrlString } from 'types/urlString'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ReplaceableOverlay } from './ReplaceableOverlay'

const svgSrcCache = new Map<string, string>()

export class ImageShapeUtil extends BaseBoxShapeUtil<ImageShape> {
  static override type = 'image' as const

  shouldDeselectAfterTranslating = false

  static override props = {
    w: T.positiveNumber,
    h: T.positiveNumber,
    playing: T.boolean,
    url: T.string.nullable(),
    assetId: assetIdValidator.nullable(),
    crop: T.object({
      topLeft: T.object({
        x: T.number,
        y: T.number,
        z: T.number.optional(),
      }),
      bottomRight: T.object({
        x: T.number,
        y: T.number,
        z: T.number.optional(),
      }),
    }).nullable(),
    cornerRounding: CornerRoundingStyle,
    strokeWidth: StrokeWidthStyle,
    strokeColor: StrokeColorStyle,
    isReplaceable: T.boolean,
    wasReplaced: T.boolean,
    isBackground: T.boolean,
    removeBackground: RemoveBackgroundStyle,
    isLogo: T.boolean,
    isEditable: T.boolean,
    isAutoUpdatable: T.boolean,
  }

  static override migrations = imageShapeMigrations

  override getDefaultProps(): ImageShapeProps {
    return {
      w: 100,
      h: 100,
      assetId: null,
      playing: false,
      url: '',
      crop: null,
      cornerRounding: 0,
      strokeColor: '#000000',
      strokeWidth: 0,
      isReplaceable: false,
      wasReplaced: false,
      isBackground: false,
      removeBackground: null,
      isLogo: false,
      isEditable: true,
      isAutoUpdatable: true,
    }
  }

  override isAspectRatioLocked = () => true
  override canCrop = () => true
  canUnmount = () => false

  onBeforeCreate: TLOnBeforeCreateHandler<ImageShape> = (shape) => {
    const svgAsset = this.getSvgAsset(shape)
    if (!svgAsset) return shape

    let sanitisedShape: ImageShape = {
      ...shape,
      props: {
        ...shape.props,
        removeBackground: null,
      },
    }

    this.getSvgAssetAsString(sanitisedShape).then((svg) => {
      this.editor.updateShape({
        ...shape,
        meta: {
          colorSet: shape.meta.colorSet || extractColors(svg),
        },
      })
    })

    return sanitisedShape
  }

  onBeforeUpdate: TLOnBeforeUpdateHandler<ImageShape> = (prev, next) => {
    if (!prev.props.isEditable) {
      return { ...prev, props: { ...prev.props, isEditable: next.props.isEditable } }
    }

    const svgAsset = this.getSvgAsset(next)

    let sanitisedShape: ImageShape = {
      ...next,
      props: {
        ...next.props,
        removeBackground: svgAsset ? null : next.props.removeBackground,
      },
    }

    if ('fillColor' in sanitisedShape.props) {
      const { fillColor, ...props } = sanitisedShape.props
      sanitisedShape = { ...sanitisedShape, props }
    }

    return sanitisedShape
  }

  onTranslate: TLOnTranslateHandler<ImageShape> = (initialShape) => {
    if (!initialShape.props.isEditable) {
      this.editor.snaps.clear()
    }
  }

  onTranslateEnd = () => {
    if (this.shouldDeselectAfterTranslating) {
      this.editor.selectNone()
    }
    this.shouldDeselectAfterTranslating = false
  }

  /**
   * Returns true if the shape can be replaced and has not been replaced yet
   * @param shape The shape to check
   */
  canBeReplaced = (shape: ImageShape) => shape.props.isReplaceable && !shape.props.wasReplaced

  /**
   * Returns true if the shape cannot be replaced and is not a background image
   * @param shape The shape to check
   */
  canReplace = (shape: ImageShape) => {
    return !shape.props.isBackground && !this.canBeReplaced(shape)
  }

  component(shape: ImageShape) {
    const containerStyle = getContainerStyle(shape)
    const isCropping = useIsCropping(shape.id)
    const asset = shape.props.assetId ? this.editor.getAsset(shape.props.assetId) : undefined
    const colorSet = shape.meta.colorSet
    const [imgSrc, setImgSrc] = useState(asset?.props.src || '')
    const { isMobile } = useResponsiveDesign()

    useEffect(() => {
      if (!asset || colorSet) return

      const src = asset.props.src

      if (!src) return

      setImgSrc(src)
    }, [asset, colorSet])

    useEffect(() => {
      if (!colorSet) return

      const replaceImgSrcWithColorisedSrc = async () => {
        let svgStr = await this.getSvgAssetAsString(shape)
        const newSvgStr = replaceSvgColors(svgStr, colorSet)
        setImgSrc(createBlobUrlFromSvg(newSvgStr))
      }

      replaceImgSrcWithColorisedSrc()
    }, [JSON.stringify(colorSet)]) // eslint-disable-line react-hooks/exhaustive-deps

    const isSelected = useValue('onlySelectedShape', () => shape.id === this.editor.getOnlySelectedShape()?.id, [
      this.editor,
    ])

    const showCropPreview =
      isSelected && isCropping && this.editor.isInAny('select.crop', 'select.cropping', 'select.pointing_crop_handle')

    const path = this.getCornerRoundedClipPath(shape)
    const clipPath = `path("${this.getCornerRoundedClipPath(shape)}")`

    return (
      <>
        {imgSrc && showCropPreview && (
          <div style={containerStyle}>
            <div
              className="tl-image"
              style={{
                opacity: 0.1,
                backgroundImage: `url(${imgSrc})`,
                transition: 'all 250ms ease',
              }}
              draggable={false}
            />
          </div>
        )}
        <HTMLContainer
          id={shape.id}
          style={{
            overflow: 'hidden',
            width: shape.props.w,
            height: shape.props.h,
            clipPath,
            pointerEvents: 'all',
          }}
        >
          <div
            className="tl-image-container"
            style={containerStyle}
            onPointerDown={() => {
              if (isMobile) {
                const shapeUtil = this.editor.getShapeUtil('image') as ImageShapeUtil
                shapeUtil.shouldDeselectAfterTranslating = this.editor.getSelectedShapes().length === 0
              }
            }}
          >
            {imgSrc && <div className="tl-image" style={{ backgroundImage: `url(${imgSrc})` }} draggable={false} />}
          </div>
        </HTMLContainer>
        {shape.props.strokeWidth > 0 && (
          <SVGContainer style={{ width: containerStyle.width, height: containerStyle.height }}>
            <path d={path} stroke={shape.props.strokeColor} strokeWidth={shape.props.strokeWidth} fill="none" />
          </SVGContainer>
        )}
        {this.canBeReplaced(shape) && <ReplaceableOverlay shapeId={shape.id} />}
      </>
    )
  }

  indicator(shape: ImageShape) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const isCropping = useIsCropping(shape.id)
    if (isCropping) {
      return null
    }
    return <rect width={toDomPrecision(shape.props.w)} height={toDomPrecision(shape.props.h)} />
  }

  async colorise(shape: ImageShape, newColorSet: ColorSet) {
    // If the shape doesn't have a colorSet, we can't colorise it
    if (!shape.meta.colorSet) return

    this.editor.batch(() => {
      this.editor.updateShape({
        ...shape,
        meta: {
          colorSet: newColorSet,
        },
      })
    })
  }

  getCornerRoundedClipPath = (shape: ImageShape) => {
    const vertices = this.editor.getShapeGeometry(shape).vertices
    const roundingPercent = (Math.min(shape.props.h, shape.props.w) / 100) * shape.props.cornerRounding
    return roundCorners(`M${vertices[0]}L${vertices.slice(1)}Z`, roundingPercent).path
  }

  isColorisable = (image: ImageShape) => !!image.meta.colorSet && !image.props.isLogo

  override async toSvg(shape: ImageShape) {
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    const src = await this.getSrcAsDataURI(shape)

    const image = document.createElementNS('http://www.w3.org/2000/svg', 'image')
    image.setAttributeNS('http://www.w3.org/1999/xlink', 'href', src)
    const containerStyle = getContainerStyle(shape)
    const crop = shape.props.crop

    const clipPathPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    clipPathPath.setAttribute('d', this.getCornerRoundedClipPath(shape))

    const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath')
    clipPath.setAttribute('id', `cropClipPath-${shape.id}`)
    clipPath.appendChild(clipPathPath)

    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    defs.appendChild(clipPath)
    g.appendChild(defs)

    if (containerStyle.transform && crop) {
      const { transform, width, height } = containerStyle
      const innerElement = document.createElementNS('http://www.w3.org/2000/svg', 'g')
      innerElement.setAttribute('clip-path', `url(#cropClipPath-${shape.id})`)

      image.setAttribute('width', width.toString())
      image.setAttribute('height', height.toString())
      image.style.transform = transform
      innerElement.appendChild(image)

      const border = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      border.setAttribute('d', this.getCornerRoundedClipPath(shape))
      border.setAttribute('width', width.toString())
      border.setAttribute('height', height.toString())
      border.setAttribute('fill', 'none')
      border.setAttribute('stroke', shape.props.strokeColor)
      border.setAttribute('stroke-width', shape.props.strokeWidth.toString())

      g.appendChild(innerElement)
      g.appendChild(border)
    } else {
      const width = shape.props.w.toString()
      const height = shape.props.h.toString()
      image.setAttribute('width', width)
      image.setAttribute('height', height)
      image.setAttribute('clip-path', `url(#cropClipPath-${shape.id})`)

      const border = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      border.setAttribute('d', this.getCornerRoundedClipPath(shape))
      border.setAttribute('width', width)
      border.setAttribute('height', height)
      border.setAttribute('fill', 'none')
      border.setAttribute('stroke', shape.props.strokeColor)
      border.setAttribute('stroke-width', shape.props.strokeWidth.toString())

      g.appendChild(image)
      g.appendChild(border)
    }

    return g
  }

  override onDoubleClick = (shape: ImageShape) => {
    const asset = shape.props.assetId ? this.editor.getAsset(shape.props.assetId) : undefined

    if (!asset) return

    const canPlay = asset.props.src && 'mimeType' in asset.props && asset.props.mimeType === 'image/gif'

    if (!canPlay) return

    this.editor.updateShapes([
      {
        type: 'image',
        id: shape.id,
        props: {
          playing: !shape.props.playing,
        },
      },
    ])
  }

  override onDoubleClickEdge = (shape: ImageShape) => {
    const props = shape.props
    if (!props) return

    if (this.editor.getCroppingShapeId() !== shape.id) {
      return
    }

    const crop = deepCopy(props.crop) || {
      topLeft: { x: 0, y: 0 },
      bottomRight: { x: 1, y: 1 },
    }

    // The true asset dimensions
    const w = (1 / (crop.bottomRight.x - crop.topLeft.x)) * shape.props.w
    const h = (1 / (crop.bottomRight.y - crop.topLeft.y)) * shape.props.h

    const pointDelta = new Vec2d(crop.topLeft.x * w, crop.topLeft.y * h).rot(shape.rotation)

    const partial: TLShapePartial<ImageShape> = {
      id: shape.id,
      type: shape.type,
      x: shape.x - pointDelta.x,
      y: shape.y - pointDelta.y,
      props: {
        crop: {
          topLeft: { x: 0, y: 0 },
          bottomRight: { x: 1, y: 1 },
        },
        w,
        h,
      },
    }

    this.editor.updateShapes([partial])
  }

  getColors = (shape: ImageShape) => {
    const colors = new CustomEqualitySet<SolidColor>(isEqual)

    if (shape.props.strokeWidth > 0) {
      colors.add({ type: 'solidColor', color: shape.props.strokeColor, opacity: 1 })
    }

    if (shape.meta.colorSet) {
      if (Object.entries(shape.meta.colorSet).length === 0) {
        colors.add({ type: 'solidColor', color: '#000000', opacity: 1 })
      } else {
        Object.values(shape.meta.colorSet).forEach((color) => {
          colors.add({ type: 'solidColor', color, opacity: 1 })
        })
      }
    }

    return colors
  }

  /**
   * Updates the isReplaceable property of the shape and sets the wasReplaced property to false
   * @param shape The shape to update
   * @param isReplaceable The new value of isReplaceable
   * @returns void
   */
  setReplaceable = (shape: ImageShape, isReplaceable: boolean) => {
    this.editor.updateShapes([
      {
        ...shape,
        props: {
          isReplaceable,
          wasReplaced: false,
        },
      },
    ])
  }

  /**
   * Replaces the image with a new one with the given assetId.
   */
  replace = async (
    shape: ImageShape,
    assetId: TLAssetId,
    props?: Partial<ImageShapeProps>,
    opts?: {
      fit?: 'contain' | 'cover'
    },
  ) => {
    const asset = this.editor.getAsset(assetId) as TLImageAsset | undefined
    if (!asset) return

    const { fit = 'contain' } = opts || {}

    const currentBoundingBox = this.getGeometry(shape).getBounds()
    const fittedImage = fitAndPosition(
      { width: currentBoundingBox.w, height: currentBoundingBox.h },
      { width: asset.props.w, height: asset.props.h },
      fit,
      '50%',
      '50%',
    )
    this.editor.batch(() => {
      this.editor.deleteShapes([shape.id])
      this.editor.createShape({
        type: 'image',
        parentId: shape.parentId,
        x: shape.x + fittedImage.x,
        y: shape.y + fittedImage.y,
        index: shape.index,
        props: {
          ...shape.props,
          ...props,
          crop: null,
          assetId: asset.id,
          w: fittedImage.width,
          h: fittedImage.height,
          isReplaceable: shape.props.isReplaceable,
          wasReplaced: true,
        },
      })
    })
  }

  /**
   * Creates a new background image for the given parent with the given asset.
   * If the parent already has a background image, it deletes that background
   * image first before creating the new one.
   *
   * @param parentId The id of the parent shape
   * @param assetId The id of the asset to use as the background image
   * @returns ImageShape | undefined
   */
  createAsBackground = (parentId: TLShapeId, assetId: TLAssetId) => {
    const asset = this.editor.getAsset(assetId) as TLImageAsset | undefined
    if (!asset) return

    const parent = this.editor.getShape<FrameShape>(parentId)
    if (!parent) return

    const existingBackgroundId = this.editor.getSortedChildIdsForParent(parent).find((id) => {
      const child = this.editor.getShape<ImageShape>(id)
      return child?.type === 'image' && child.props.isBackground
    })

    const fittedImage = fitAndPosition(
      { width: parent.props.w, height: parent.props.h },
      { width: asset.props.w, height: asset.props.h },
      'cover',
      '50%',
      '50%',
    )

    const id = createShapeId()
    this.editor.batch(() => {
      if (existingBackgroundId) {
        this.editor.deleteShapes([existingBackgroundId])
      }

      this.editor.createShape({
        id,
        type: 'image',
        parentId,
        x: fittedImage.x,
        y: fittedImage.y,
        props: {
          assetId: asset.id,
          w: fittedImage.width,
          h: fittedImage.height,
          isBackground: true,
        },
      })
      this.editor.sendToBack([id])
    })

    return this.editor.getShape<ImageShape>(id)
  }

  /**
   * Sets the isBackground property of the shape to the given value.
   * If the shape is being set as a background image, it will also send the
   * shape to the back. If the shape is being set as a background image and
   * there is already a background image for the parent, it will set the
   * isBackground property of the existing background image to false.
   *
   * @param shape The shape to update
   * @param isBackground The new value of isBackground
   */
  setIsBackground = (shape: ImageShape, isBackground: boolean) => {
    if (shape.props.isBackground === isBackground) return

    if (!isBackground) {
      this.editor.updateShape({
        ...shape,
        props: {
          ...shape.props,
          isBackground: false,
        },
      })
      return
    }

    this.editor.batch(() => {
      const existingBackground = getBackgroundImage(this.editor, shape.parentId)

      if (existingBackground) {
        this.editor.updateShape({
          ...existingBackground,
          props: {
            ...existingBackground.props,
            isBackground: false,
          },
        })
      }
      this.editor.updateShape({
        ...shape,
        props: {
          ...shape.props,
          isBackground: true,
        },
      })
      this.editor.sendToBack([shape.id])
    })
  }

  canRemoveBackground = (shape: ImageShape) => {
    const { assetId } = shape.props
    if (!assetId) return

    const asset = this.editor.getAsset(assetId) as TLImageAsset | undefined
    if (!asset || !asset.meta.mediaFileId) return false

    const isSvg = asset.props.mimeType === 'image/svg+xml'
    if (typeof asset.meta.mediaFileId !== 'string' || isSvg) return false

    return !isUrlString(asset.meta.mediaFileId)
  }

  /**
   * Enables or disables the removeBackground property of the shape, and sets
   * the assetId to the new asset. The new asset must exist in the editor's
   * asset store.
   *
   * @param shape The shape to update
   * @param enabled Whether to enable or disable the removeBackground property
   */
  setRemoveBackground = (shape: ImageShape, enabled: boolean) => {
    if (enabled === !!shape.props.removeBackground) return

    const { assetId } = shape.props
    if (!assetId) return

    const currentAsset = this.editor.getAsset(assetId) as TLImageAsset | undefined
    if (!currentAsset) return

    const mediaFileId = currentAsset.meta.mediaFileId as string | undefined
    if (!mediaFileId) return

    const transformations = enabled ? { remove_background: { crop: true } } : {}
    const newAsset = findAssetByTransformations(this.editor, mediaFileId, transformations)

    if (!newAsset) {
      return
    }

    this.editor.batch(() => {
      const scale = Math.min(shape.props.w / newAsset.props.w, shape.props.h / newAsset.props.h)
      this.editor.updateShape({
        ...shape,
        props: {
          ...shape.props,
          w: newAsset.props.w * scale,
          h: newAsset.props.h * scale,
          assetId: newAsset.id,
          removeBackground: enabled ? { crop: true } : null,
        },
      })
    })
  }

  fitShapeToCanvas = (shape: ImageShape) => {
    const parentFrame = this.editor.findShapeAncestor(
      shape.id,
      (shape): shape is FrameShape => shape.type === 'frame',
    ) as FrameShape | undefined
    if (!parentFrame) return

    const imgFit = fitAndPosition(
      { width: parentFrame.props.w, height: parentFrame.props.h },
      { width: shape.props.w, height: shape.props.h },
      'cover',
      '50%',
      '50%',
    )

    this.editor.updateShape({
      ...shape,
      x: imgFit.x,
      y: imgFit.y,
      props: {
        ...shape.props,
        w: imgFit.width,
        h: imgFit.height,
      },
    })
  }

  private getSvgAsset(shape: ImageShape) {
    if (!shape.props.assetId) return

    const asset = this.editor.getAsset(shape.props.assetId) as TLImageAsset | undefined

    if (asset && isSvgAsset(asset)) return asset
  }

  private async getSvgAssetAsString(shape: ImageShape) {
    const svgAsset = this.getSvgAsset(shape)

    if (!svgAsset) throw new Error('No SVG asset found')
    if (svgSrcCache.has(svgAsset.props.src)) {
      return svgSrcCache.get(svgAsset.props.src) as string
    }

    const res = await fetch(noCacheUrl(svgAsset.props.src))
    const text = await res.text()
    svgSrcCache.set(svgAsset.props.src, text)
    return text
  }

  private async getSrcAsDataURI(shape: ImageShape) {
    const colorSet = shape.meta.colorSet
    const asset = shape.props.assetId ? (this.editor.getAsset(shape.props.assetId) as TLImageAsset | undefined) : null

    if (asset && isSvgAsset(asset) && colorSet) {
      const svgStr = replaceSvgColors(await this.getSvgAssetAsString(shape), colorSet)
      return `data:image/svg+xml;base64,${btoa(svgStr)}`
    }

    const src = asset?.props.src || ''

    if (src && src.startsWith('http')) {
      return (await getDataURIFromURL(noCacheUrl(src))) || ''
    }

    return ''
  }

  hideRotateHandle = () => true
}
